import React, { useCallback, useEffect, useState } from 'react';
import { MyCompanyType, UIPositionStatus } from 'types';
import AMCompaniesTabsComponent from './AMCompaniesTabsComponent';
import AMCompanyCard from './AMCompanyCard';
import AMCompanyDrawer from './AMCompanyDrawer';
import classNames from 'classnames';
import { Pagination, Spinner, TextInput, ToggleSwitch } from 'flowbite-react';
import { TbSearch } from 'react-icons/tb';
import FilterDrawer from '../recruiterPositions/filterDrawer/FilterDrawer';
import CompaniesFiltersForm from '../recruiterPositions/filterDrawer/CompaniesFiltersForm';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import NoRecruiterPositionsContainer from '../recruiterPositions/NoRecruiterPositionsContainer';
import { debounce } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { useUserContext } from 'src/context/UserProvider';
import NewCreatingPositionLoader from '../position/NewCreatingPositionLoader';

interface Props {
	activeCompanies: MyCompanyType[];
	selfCompanies: MyCompanyType[];
	allCompanies: MyCompanyType[];
	handleCreateCompany: () => void;
	disableCreate: boolean;
	isLoadingCompanies: boolean;
	totalCompaniesCount: number;
	totalSelfCompaniesCount: number;
	totalActiveCompaniesCount: number;
}

const AMCompaniesContainer = ({
	allCompanies,
	activeCompanies,
	selfCompanies,
	isLoadingCompanies,
	totalSelfCompaniesCount,
	totalCompaniesCount,
	totalActiveCompaniesCount,
}: Props) => {
	const limit = 12;
	const isMobile = useMediaQuery('(max-width:960px)');
	const [searchParams, setSearchParams] = useSearchParams();
	const { filterApplied, setFilterApplied } = useRecommendationsContext();
	const { user } = useUserContext();

	const [isCompanyDrawerOpen, setIsCompanyDrawerOpen] = useState<boolean>(false);
	const [showNewlyCreated, setShowNewlyCreated] = useState<boolean>(false);
	const [company, setCompany] = useState<MyCompanyType['company']>();
	const [tab, setTab] = useState<UIPositionStatus | undefined>(UIPositionStatus.ACTIVE);
	const [filteredCompanies, setFilteredCompanies] = useState<MyCompanyType[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [searchValue, setSearchValue] = useState('');

	const handleViewCompany = (company: MyCompanyType['company']) => {
		setCompany(company);
		setIsCompanyDrawerOpen(true);
	};

	const changeHandler = (search: string) => {
		setCurrentPage(1);
		searchParams.set('page', String(1));
		if (search) {
			setFilterApplied(true);
			searchParams.set('name', String(search?.toLowerCase()?.trim()));
		} else {
			setFilterApplied(false);
			searchParams.delete('name');
		}
		setSearchParams(searchParams);
	};

	const debouncedChangeHandler = useCallback(debounce(changeHandler, 1000), []);

	useEffect(() => {
		if (tab === UIPositionStatus.ACTIVE) {
			setFilteredCompanies(activeCompanies);
		} else if (tab === UIPositionStatus.SELF) {
			setFilteredCompanies(selfCompanies);
		} else {
			setFilteredCompanies(allCompanies);
		}
	}, [allCompanies, activeCompanies, selfCompanies, tab]);

	useEffect(() => {
		if (searchParams) {
			const page = +(searchParams.get('page') || 1);
			const selectedTab = searchParams.get('tab') || UIPositionStatus.ACTIVE;
			const name = searchParams.get('name');
			const showNewlyCreated = searchParams.get('showNewlyCreated');
			setTab(selectedTab as UIPositionStatus);
			setCurrentPage(page);
			setSearchValue(name || '');
			setShowNewlyCreated(showNewlyCreated ? true : false);

			if (name || showNewlyCreated) {
				setFilterApplied(true);
			}
		}
	}, [searchParams]);

	useEffect(() => {
		if (user?.flags.isLeadGenAccountManager) {
			searchParams.set('tab', UIPositionStatus.SELF);
			setTab(UIPositionStatus.SELF);
			setSearchParams(searchParams);
		}
	}, [user]);

	const allCompaniesPages = Math.ceil(totalCompaniesCount / limit);
	const selfCompaniesPages = Math.ceil(totalSelfCompaniesCount / limit);
	const activeCompaniesPages = Math.ceil(totalActiveCompaniesCount / limit);
	const allPages =
		tab === UIPositionStatus.ALL_COMPANIES
			? allCompaniesPages
			: tab === UIPositionStatus.SELF
				? selfCompaniesPages
				: activeCompaniesPages;

	return (
		<>
			<div className="hidden md:flex justify-between items-center py-2 pt-6 sm:pt-[10px] border-b border-[#EBEBF0] dark:border-[#555770] border-solid">
				<div className="text-lg font-semibold dark:text-white">Companies</div>
			</div>

			<div className="pt-4">
				<div className={classNames('flex mb-[10px] gap-2', 'flex-col')}>
					<div className="w-full flex flex-wrap items-center justify-between gap-[10px]">
						{/* Top Tabs for recommended and connected */}
						<AMCompaniesTabsComponent
							tab={tab}
							setTab={setTab}
							self={totalSelfCompaniesCount}
							activeCompanies={totalActiveCompaniesCount}
							allCompanies={totalCompaniesCount}
							onChange={data => {
								searchParams.set('tab', data || '');
								if (currentPage !== 1) {
									setCurrentPage(1);
								}
								searchParams.set('page', String(1));
								setSearchParams(searchParams);
							}}
						/>

						<div className="w-full md:mt-0 md:max-w-[522px] flex items-center gap-[12px] justify-stretch">
							{/* Search Input */}
							<TextInput
								icon={() => <TbSearch color="#C7C9D9" />}
								className="w-full [&_input]:bg-white"
								placeholder="Search Company"
								name="search"
								value={searchValue}
								disabled={isLoadingCompanies}
								rightIcon={() => (isLoadingCompanies ? <Spinner /> : null)}
								onChange={e => {
									setSearchValue(e.target.value);
									debouncedChangeHandler(e.target.value);
								}}
							/>

							{/* Filter */}
							<FilterDrawer
								filterDrawerHeader={
									<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4 md:border-b md:pb-4">
										Filter by
									</h1>
								}
								filterAppliedValue={filterApplied}
								disabled={isLoadingCompanies}
								filterDrawerBody={(filter: any) => {
									return (
										<div className="px-3 py-4">
											<CompaniesFiltersForm
												onApply={() => {
													setFilterApplied(true);
													filter.setFilterApplied(true);
												}}
												onClear={() => {
													setFilterApplied(false);
													setShowNewlyCreated(false);
													setSearchValue('');
													searchParams.delete('showNewlyCreated');
													searchParams.delete('name');
													setSearchParams(searchParams);
													filter.setFilterApplied(false);
												}}
												onClose={() => {
													filter.setShowFilterDrawer(false);
												}}
											/>
										</div>
									);
								}}
							/>
						</div>
					</div>

					<div
						className={`${tab !== UIPositionStatus.SELF || allPages > 1 ? 'min-h-[38px]' : ''
							} flex flex-wrap items-center justify-between gap-2`}
					>
						{tab !== UIPositionStatus.SELF ? (
							<ToggleSwitch
								theme={{
									toggle: {
										checked: {
											color: {
												blue: 'bg-[#7777E7] border-[#7777E7]',
											},
										},
									},
								}}
								checked={showNewlyCreated}
								label={'Newly Created'}
								disabled={isLoadingCompanies}
								onChange={data => {
									setFilterApplied(data);
									setShowNewlyCreated(prev => !prev);
									if (data) {
										searchParams.set('showNewlyCreated', String(data));
									} else {
										searchParams.delete('showNewlyCreated');
									}
									setSearchParams(searchParams);
								}}
							/>
						) : null}

						{filteredCompanies.length && allPages > 1 ? (
							<div className="flex justify-center">
								<Pagination
									showIcons
									color="purplePlaced"
									className="-mt-2"
									layout={isMobile ? 'navigation' : 'pagination'}
									currentPage={currentPage}
									totalPages={allPages}
									onPageChange={page => {
										searchParams.set('page', String(page || 1));
										setSearchParams(searchParams);
										if (page !== currentPage) {
											setCurrentPage(page);
										}
									}}
								/>
							</div>
						) : null}
					</div>
				</div>
			</div>

			{isLoadingCompanies ? (
				<div className="flex items-center justify-center min-h-[300px]">
					{/* <Spinner /> */}
					<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
				</div>
			) : (
				<div className="flex justify-center my-5">
					{tab === UIPositionStatus.SELF ? (
						<div className="flex flex-col gap-4 w-full">
							<div className="font-bold">Action Pending</div>
							<div className="flex flex-wrap gap-7 sm:flex-col w-full sm:gap-4">
								{filteredCompanies
									.filter(c => c.company.isLead)
									.map((company, i) => {
										// const bdPOC1 = Array.isArray(company?.company?.leadGenMeta) ? company?.company?.leadGenMeta[0] : undefined;							
										const bdPOCData = company?.company?.leadGenMeta;
										let bdPOC;

										if (Array.isArray(bdPOCData) && bdPOCData.length > 0) {
											// If leadGenMeta is an array as it should be
											bdPOC = [...bdPOCData].sort((a, b) =>
												new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
											)[0];
										} else if (bdPOCData?.leadGen && Array.isArray(bdPOCData.leadGen)) {
											// If leadGenMeta is wrapped in an object with leadGen array
											bdPOC = [...bdPOCData.leadGen].sort((a, b) =>
												new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
											)[0];
										}

										// Add this check
										const hasLeadGenMeta = Array.isArray(company?.company?.leadGenMeta) && company?.company?.leadGenMeta.length > 0;
										const leadGenData = hasLeadGenMeta ? company.company.leadGenMeta : [];

										// Defensive access to BD poc data with multiple fallback paths
										const bdPOCName = hasLeadGenMeta ?
											(bdPOC?.bdPOC?.name || bdPOC?.leadGen?.bdPOC?.name || null) : null;

										const bdPOCId = hasLeadGenMeta ?
											(bdPOC?.bdPOC?._id || bdPOC?.leadGen?.bdPOC?._id || null) : null;
										return (
											<div key={i} className="sm:w-full md:min-w-[352px] md:w-[calc(33.3%-19px)]">
												<AMCompanyCard
													bdPOCName={bdPOCName}
													bdPOCId={bdPOCId}
													isActive={true}
													companyObj={company}
													handleViewCompanyDetails={handleViewCompany}
													leadGen={leadGenData}
												/>
											</div>
										)
									})}
							</div>
							<div className="font-bold border-t-[1px] border-t-gray-200 mt-4 pt-4">Qualified/Disqualified Leads</div>
							<div className="flex flex-wrap gap-7 sm:flex-col w-full sm:gap-4">
								{filteredCompanies
									.filter(c => !c.company.isLead)
									.map((company, i) => {
										// const bdPOC1 = Array.isArray(company?.company?.leadGenMeta) ? company?.company?.leadGenMeta[0] : undefined;							
										const bdPOCData = company?.company?.leadGenMeta;
										let bdPOC;

										if (Array.isArray(bdPOCData) && bdPOCData.length > 0) {
											// If leadGenMeta is an array as it should be
											bdPOC = [...bdPOCData].sort((a, b) =>
												new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
											)[0];
										} else if (bdPOCData?.leadGen && Array.isArray(bdPOCData.leadGen)) {
											// If leadGenMeta is wrapped in an object with leadGen array
											bdPOC = [...bdPOCData.leadGen].sort((a, b) =>
												new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
											)[0];
										}

										// Add this check
										const hasLeadGenMeta = Array.isArray(company?.company?.leadGenMeta) && company?.company?.leadGenMeta.length > 0;
										const leadGenData = hasLeadGenMeta ? company.company.leadGenMeta : [];

										// Defensive access to BD poc data with multiple fallback paths
										const bdPOCName = hasLeadGenMeta ?
											(bdPOC?.bdPOC?.name || bdPOC?.leadGen?.bdPOC?.name || null) : null;

										const bdPOCId = hasLeadGenMeta ?
											(bdPOC?.bdPOC?._id || bdPOC?.leadGen?.bdPOC?._id || null) : null;
										return (
											<div key={i} className="sm:w-full md:min-w-[352px] md:w-[calc(33.3%-19px)]">
												<AMCompanyCard
													bdPOCName={bdPOCName}
													bdPOCId={bdPOCId}
													isActive={true}
													companyObj={company}
													handleViewCompanyDetails={handleViewCompany}
													leadGen={leadGenData}
												/>
											</div>
										)
									})}
							</div>
						</div>
					) : filteredCompanies.length ? (
						<div className="flex flex-wrap gap-7 sm:flex-col w-full sm:gap-4">
							{filteredCompanies.map((company, i) => {
								// const bdPOC1 = Array.isArray(company?.company?.leadGenMeta) ? company?.company?.leadGenMeta[0] : undefined;							
								const bdPOCData = company?.company?.leadGenMeta;
								let bdPOC;

								if (Array.isArray(bdPOCData) && bdPOCData.length > 0) {
									// If leadGenMeta is an array as it should be
									bdPOC = [...bdPOCData].sort((a, b) =>
										new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
									)[0];
								} else if (bdPOCData?.leadGen && Array.isArray(bdPOCData.leadGen)) {
									// If leadGenMeta is wrapped in an object with leadGen array
									bdPOC = [...bdPOCData.leadGen].sort((a, b) =>
										new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
									)[0];
								}

								// Add this check
								const hasLeadGenMeta = Array.isArray(company?.company?.leadGenMeta) && company?.company?.leadGenMeta.length > 0;
								const leadGenData = hasLeadGenMeta ? company.company.leadGenMeta : [];

								// Defensive access to BD poc data with multiple fallback paths
								const bdPOCName = hasLeadGenMeta ?
									(bdPOC?.bdPOC?.name || bdPOC?.leadGen?.bdPOC?.name || null) : null;

								const bdPOCId = hasLeadGenMeta ?
									(bdPOC?.bdPOC?._id || bdPOC?.leadGen?.bdPOC?._id || null) : null;

								return (
									<div key={i} className="sm:w-full md:min-w-[352px] md:w-[calc(33.3%-19px)]">
										<AMCompanyCard
											isActive={tab === UIPositionStatus.ALL_COMPANIES}
											bdPOCName={bdPOCName}
											bdPOCId={bdPOCId}
											companyObj={company}
											handleViewCompanyDetails={handleViewCompany}
											leadGen={leadGenData}
										/>
									</div>
								)
							})}
						</div>
					) : (
						<div className=" mt-4 w-full h-full flex flex-col justify-center items-center">
							<NoRecruiterPositionsContainer
								title={tab === UIPositionStatus.ALL_COMPANIES ? 'No active companies' : 'No companies yet!'}
								description=""
								showAction={false}
							/>
						</div>
					)}
				</div>
			)}

			<AMCompanyDrawer
				isCompanyDrawerOpen={isCompanyDrawerOpen}
				company={company}
				setIsCompanyDrawerOpen={setIsCompanyDrawerOpen}
				closeDrawer={() => setIsCompanyDrawerOpen(false)}
			/>

			{filteredCompanies.length && allPages > 1 ? (
				<div className="flex justify-center">
					<Pagination
						showIcons
						color="purplePlaced"
						className="-mt-2"
						layout={isMobile ? 'navigation' : 'pagination'}
						currentPage={currentPage}
						totalPages={allPages}
						onPageChange={page => {
							searchParams.set('page', String(page || 1));
							setSearchParams(searchParams);
							if (page !== currentPage) {
								setCurrentPage(page);
							}
						}}
					/>
				</div>
			) : null}
		</>
	);
};

export default AMCompaniesContainer;
